import { EnumLocalStorageKeys } from '_constants'
import { useCallback } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { SIGNUP_STEPS } from '../config/signup'
import {
  EnumSignupErrors,
  EnumSignupSteps,
  EnumSignupType,
  SignupData,
} from '../types/signup'

interface SignupStore {
  currentStep: EnumSignupSteps
  signupData: SignupData
  signupError?: EnumSignupErrors | null
  signupType: EnumSignupType
}

interface SignupActions {
  goToStep: (signupStep: EnumSignupSteps) => void
  goToNextStep: () => void
  goToPreviousStep: () => void
  updateSignupData: (signuData: Partial<SignupData>) => void
  setSignupError: (signupError: EnumSignupErrors) => void
  clearSignupError: () => void
  setSignupType: (signupType: EnumSignupType) => void
  resetSignupData: () => void
}

const DEFAULT_SIGNUP_STORE: SignupStore = {
  currentStep: SIGNUP_STEPS[0].stepName,
  signupData: {
    email: undefined,
    firstname: undefined,
    lastname: undefined,
    password: undefined,
    userAcceptsTerms: false,
    allOptionalConsent: false,
    hasConsentedForMarketing: false,
    hasConsentedForThirdPartyCommunication: false,
    userAcceptsCommercialSolicitation: false,
    publicSponsorshipVoucher: undefined,
  },
  signupType: EnumSignupType.DEFAULT_OFFER,
}

const useSignupStore = create<SignupStore>()(
  persist(() => DEFAULT_SIGNUP_STORE, {
    name: EnumLocalStorageKeys.SIGNUP_FORM,
    partialize: (state) => {
      const { password, ...signupDataToPersist } = state.signupData
      return { ...state, signupData: signupDataToPersist }
    },
  })
)

export const useSignupStoreStep = () =>
  useSignupStore((state) => state.currentStep)

export const useSignupStoreData = () =>
  useSignupStore((state) => state.signupData)

export const useSignupStoreType = () =>
  useSignupStore((state) => state.signupType)

export const useSignupStoreError = () =>
  useSignupStore((state) => state.signupError)

export const useSignupStoreActions = (): SignupActions => {
  const { setState } = useSignupStore

  const goToStep: SignupActions['goToStep'] = useCallback(
    (signupStep) => setState({ currentStep: signupStep }),
    [setState]
  )

  const goToNextStep: SignupActions['goToNextStep'] = useCallback(() => {
    setState(({ currentStep }) => {
      const currentStepIndex = SIGNUP_STEPS.findIndex(
        ({ stepName }) => stepName === currentStep
      )
      const isLastStep = currentStepIndex >= SIGNUP_STEPS.length - 1

      if (isLastStep) {
        return { currentStep }
      }

      const nextStep = SIGNUP_STEPS[currentStepIndex + 1].stepName

      return { currentStep: nextStep }
    })
  }, [setState])

  const goToPreviousStep: SignupActions['goToPreviousStep'] =
    useCallback(() => {
      setState(({ currentStep }) => {
        const currentStepIndex = SIGNUP_STEPS.findIndex(
          ({ stepName }) => stepName === currentStep
        )
        const isFirstStep = currentStepIndex === 0

        if (isFirstStep) {
          return { currentStep }
        }

        const prevStep = SIGNUP_STEPS[currentStepIndex - 1].stepName

        return { currentStep: prevStep }
      })
    }, [setState])

  const updateSignupData: SignupActions['updateSignupData'] = useCallback(
    (newSignupData: Partial<SignupData>) =>
      setState(({ signupData }) => ({
        signupData: { ...signupData, ...newSignupData },
      })),
    [setState]
  )

  const setSignupError: SignupActions['setSignupError'] = useCallback(
    (signupError) => setState({ signupError }),
    [setState]
  )

  const resetSignupData: SignupActions['resetSignupData'] = useCallback(
    () => setState({ signupData: DEFAULT_SIGNUP_STORE.signupData }),
    [setState]
  )

  const clearSignupError: SignupActions['clearSignupError'] = useCallback(
    () => setState({ signupError: null }),
    [setState]
  )

  const setSignupType: SignupActions['setSignupType'] = useCallback(
    (signupType) => setState({ signupType }),
    [setState]
  )

  return {
    goToStep,
    goToNextStep,
    goToPreviousStep,
    updateSignupData,
    setSignupError,
    clearSignupError,
    setSignupType,
    resetSignupData,
  }
}
